import React from "react";
import { Box, IconButton } from "@mui/material";

// Multi lingual support
import { t } from "i18next";

// Compliance status
import {
  EComplianceStatus,
  ECropStatus,
  EFieldApprovalStatus,
  EFieldOverlapStatus,
  EUserRoles,
  IUser,
  Severity,
} from "../commonTypes/commonTypes";

// Data formatter types
import {
  IFieldLocationData,
  IFormattedApprovalStatusProps,
  IFormattedComplianceStatusProps,
  IFormattedCropStatusProps,
  IFormattedOverlapStatusProps,
} from "./CommonDataFormatterTypes";
import { ELandPlotTypes } from "../../../components/farmersData/fieldsManagement/addUpdateField/addUpdateField/AddUpdateFieldTypes";

// Format date
export const getFormattedDate = (dateString: string): string => {
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "-";
  return `${String(date.getDate()).padStart(2, "0")}-${String(date.getMonth() + 1).padStart(2, "0")}-${date.getFullYear()}`;
};

// Formatted compliance status
export const getFormattedComplianceStatus: React.FC<
  IFormattedComplianceStatusProps
> = ({ status }) => {
  const statusMap: Record<
    EComplianceStatus,
    { className: string; label: string }
  > = {
    [EComplianceStatus.PENDING_REVIEW]: {
      className: "compliance-status-pending",
      label: t("commonStatus.complianceStatus.pendingReview"),
    },
    [EComplianceStatus.COMPLIANT]: {
      className: "compliance-status-compliant",
      label: t("commonStatus.complianceStatus.compliant"),
    },
    [EComplianceStatus.NOT_COMPLIANT]: {
      className: "compliance-status-notcompliant",
      label: t("commonStatus.complianceStatus.notCompliant"),
    },
  };

  return (
    <div
      className={`status-div ${statusMap[status as EComplianceStatus]?.className || ""}`}
    >
      {statusMap[status as EComplianceStatus]?.label || "-"}
    </div>
  );
};

// Get formatted approval status
export const getFormattedApprovalStatus: React.FC<
  IFormattedApprovalStatusProps
> = ({ status }) => {
  const statusMap: Record<
    EFieldApprovalStatus,
    { className: string; label: string }
  > = {
    [EFieldApprovalStatus.PENDING]: {
      className: "approval-status-pending",
      label: t("commonStatus.fieldApprovalStatus.pending"),
    },
    [EFieldApprovalStatus.APPROVED]: {
      className: "approval-status-approved",
      label: t("commonStatus.fieldApprovalStatus.approved"),
    },
    [EFieldApprovalStatus.REJECTED]: {
      className: "approval-status-rejected",
      label: t("commonStatus.fieldApprovalStatus.rejected"),
    },
  };

  return (
    <div className={`status-div ${statusMap[status]?.className || ""}`}>
      {statusMap[status]?.label || "-"}
    </div>
  );
};

// Get formatted overlap status
export const getFormattedOverlapStatus: React.FC<
  IFormattedOverlapStatusProps
> = ({ status }) => {
  const statusMap: Record<
    EFieldOverlapStatus,
    { className: string; label: string }
  > = {
    [EFieldOverlapStatus.PENDING]: {
      className: "overlap-status-pending",
      label: t("commonStatus.fieldOverlapStatus.pending"),
    },
    [EFieldOverlapStatus.NO_OVERLAP]: {
      className: "overlap-status-clear",
      label: t("commonStatus.fieldOverlapStatus.clear"),
    },
    [EFieldOverlapStatus.OVERLAP]: {
      className: "overlap-status-overlap",
      label: t("commonStatus.fieldOverlapStatus.overlapping"),
    },
  };

  return (
    <div className={`status-div ${statusMap[status]?.className || ""}`}>
      {statusMap[status]?.label || "-"}
    </div>
  );
};

// Get crop status
export const getFormattedCropStatus: React.FC<IFormattedCropStatusProps> = ({
  status,
}) => {
  const statusMap: Record<ECropStatus, { className: string; label: string }> = {
    [ECropStatus.PREPARE]: {
      className: "crop-status-prepare",
      label: t("commonStatus.cropStatus.prepare"),
    },
    [ECropStatus.ONGOING]: {
      className: "crop-status-ongoing",
      label: t("commonStatus.cropStatus.ongoing"),
    },
    [ECropStatus.HARVESTED]: {
      className: "crop-status-harvested",
      label: t("commonStatus.cropStatus.harvested"),
    },
  };

  return (
    <div className={`status-div ${statusMap[status]?.className || ""}`}>
      {statusMap[status]?.label || "-"}
    </div>
  );
};

// Get formatted area or float value with 2 decimal places
export const getFormattedFloatValue = (value: number): number => {
  return Math.round(value * 100) / 100;
};

// Format date for server
export const getFormattedDateForServer = (dateString: string): string => {
  const [day, month, year] = dateString.split("/");
  return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
};

// Formatted date for display
export const getFormattedDateForDisplay = (dateString: string): string => {
  const [year, month, day] = dateString.split("-");
  return `${day.padStart(2, "0")}/${month.padStart(2, "0")}/${year}`;
};

// Get formatted action column
export const getFormattedActions = (
  entityId: string,
  basePath?: string,
  onEditClick?: (event: React.MouseEvent) => void,
) => {
  return (
    <a
      className="edit-icon-tble"
      href={
        basePath && basePath.trim() !== ""
          ? `${basePath}${entityId}`
          : undefined
      }
      onClick={(event) => {
        event.stopPropagation(); // ✅ prevents row click
        if (!basePath || basePath.trim() === "") {
          onEditClick?.(event);
        }
      }}
      style={{
        cursor: "pointer",
      }}
    >
      <i className="icon-edit"></i>
    </a>
  );
};

// Get formatted delete action column
export const getFormattedDeleteActions = (
  user: IUser | null,
  onClick?: (event: React.MouseEvent) => void,
) => {
  const DeleteActionsComponent: React.FC<{
    onClick?: (event: React.MouseEvent) => void;
  }> = ({ onClick: propOnDeleteClick }) => (
    <a
      className="trash-icon-tble"
      onClick={(event) => {
        event.stopPropagation(); // Prevents row click
        (propOnDeleteClick || onClick)?.(event);
      }}
      style={{
        cursor: "pointer",
      }}
    >
      <i className="icon-trash"></i>
    </a>
  );

  return user?.role === EUserRoles.admin ? <DeleteActionsComponent /> : null;
};

export const getCombinedActions = (
  entityId: string,
  user: IUser | null,
  basePath?: string,
  onEditClick?: (event: React.MouseEvent) => void,
  onClick?: (event: React.MouseEvent) => void,
) => {
  const CombinedActionsComponent: React.FC<{
    onClick?: (event: React.MouseEvent) => void;
  }> = ({ onClick: propOnDeleteClick }) => (
    // Plain block <div> with the two inline <a> icons next to each
    // other. We intentionally do NOT make this a flex container —
    // ``display: flex`` would promote the <a> icons to block flex items
    // and their ``padding: 12px`` would expand the row to ~42px (the
    // padding on inline <a> elements doesn't grow the line-box in the
    // default inline layout, which is what keeps the rows compact).
    // The icons stay side-by-side here because
    // ``overflow-wrap: anywhere`` / ``word-break: break-word`` is now
    // scoped to ``.common-table-fixed-layout td`` only, and that
    // table-layout: fixed Dealer list has a 16.66% Actions column —
    // both icons fit comfortably inline.
    <div
      style={{
        marginLeft: -12,
        whiteSpace: "nowrap",
      }}
    >
      {getFormattedActions(entityId, basePath, onEditClick)}
      {getFormattedDeleteActions(user, propOnDeleteClick || onClick)}
    </div>
  );

  return <CombinedActionsComponent />;
};

// Get formatted "Link to Report" action column.
// Opens the public farmer report URL in a new tab.
// The URL is built from the app's own origin so no env variable is needed.
// Disabled when: entityId is missing OR the farmer has no eligible fields
// (no approved field with ≥1 crop cycle).
export const getFormattedLinkToReportAction = (
  entityId: string,
  hasEligibleFields = false,
) => {
  const isDisabled = !entityId || !hasEligibleFields;
  const tooltip = t("farmer.linkReport");

  const handleClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (isDisabled) return;
    const url = `${window.location.origin}/farmer-report/${entityId}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  // Styled to match the Compliance Report column on the Field List
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <IconButton
        className="icon-file-doc"
        title={isDisabled ? "" : tooltip}
        aria-label={tooltip}
        disabled={isDisabled}
        onClick={handleClick}
        sx={{
          backgroundColor: isDisabled ? "#f0f0f0" : "#E1F0FF",
          borderRadius: "8px",
          width: "40px",
          height: "40px",
          opacity: isDisabled ? 0.4 : 1,
          cursor: isDisabled ? "not-allowed" : "pointer",
          "&:hover": {
            backgroundColor: isDisabled ? "#f0f0f0" : "#b0d4ff",
          },
          "&.Mui-disabled": {
            pointerEvents: "auto",
          },
        }}
      />
    </Box>
  );
};

// Check for location pinning field
export const isFieldTypeLocationPinning = (fieldData: any): boolean => {
  return fieldData.fieldCaptureType === ELandPlotTypes.locationPinning;
};

// Check for precise mapping field
export const isFieldTypePreciseMapping = (fieldData: any): boolean => {
  return fieldData.fieldCaptureType === ELandPlotTypes.preciseMapping;
};

export const getEntityName = (entity: string) => {
  switch (entity) {
    case t("users.entity.users"):
      return t("users.entity.user");
    case t("farmer.entity.farmers"):
      return t("farmer.entity.farmer");
    case t("field.entity.fields"):
      return t("field.entity.field");
    case t("cropCycles.entity.cropCycles"):
      return t("cropCycles.entity.cropCycle");
    case t("dealer.entity.dealers"):
      return t("dealer.entity.dealer");
    default:
      return entity;
  }
};

// Get formatted deleted status
export const getFormattedDeletedStatus = () => {
  return (
    <div className="status-div approval-status-rejected">
      {t("commonStatus.formStatus.formDeleted")}
    </div>
  );
};

export const navigateToGoogleMaps = (
  fieldData: IFieldLocationData | null,
  setShowToaster?: (show: boolean, message: string, severity: Severity) => void,
) => {
  if (!fieldData) {
    setShowToaster?.(true, t("fieldDetails.unableToOpenLocation"), "error");
    return;
  }

  // centroid (pinned location)
  const centroidCoords = fieldData.centroid?.geometry?.coordinates;
  let latitude = centroidCoords?.[1] ?? null;
  let longitude = centroidCoords?.[0] ?? null;

  // boundary (first point of polygon)
  if (latitude === null || longitude === null) {
    const boundaryCoords = fieldData.boundary?.geometry?.coordinates?.[0]?.[0];
    latitude = boundaryCoords?.[1] ?? null;
    longitude = boundaryCoords?.[0] ?? null;
  }

  // Validate numbers
  const isValid =
    typeof latitude === "number" &&
    !Number.isNaN(latitude) &&
    typeof longitude === "number" &&
    !Number.isNaN(longitude);

  if (isValid) {
    const googleMapsUrl = `https://www.google.com/maps?q=${latitude},${longitude}`;
    window.open(googleMapsUrl, "_blank", "noopener,noreferrer");
  } else {
    setShowToaster?.(true, t("fieldDetails.unableToOpenLocation"), "error");
  }
};

// R14 — EUDR compliance result text colours ("EUDR logic – Text Colour When
// Displaying on UI"). The report shows the FULL result label (the "a."/"b."
// option-letter prefix was removed per the latest spec), so we match on the
// exact label string (trimmed, case-insensitive). The backend emits these
// labels in English regardless of the app language, so English matching is
// safe, and each label is field-unique so the topic isn't needed to key.
//
//   * RED  — non-compliant / negative result values.
//   * BLUE — the multi-option (d3, d5) indicative / positive values.
//   * default (black, undefined) — binary compliant values and anything else.
//
// Returns undefined for a default value so callers can spread it into an sx
// ``color`` without forcing a colour.
const EUDR_RED_VALUES = new Set<string>([
  "deforestation present", // c5
  "overlap with strict pa", // d1
  "native or customary land tenure", // d3 (g)
  "other (please describe)", // d3 (h)
  "no documents", // d3 (i)
  // d5 (f)
  "located in protected area or restricted use area without documentation (illegal use)",
  "plot located on non-compliant area", // e1
  "non-compliant chemical use detected", // e2
  "env non-compliance detected", // e3
]);

const EUDR_BLUE_VALUES = new Set<string>([
  // d3 (a–f)
  "official land title (with evidence)",
  "official right of use (with evidence)",
  "local government documentation (with evidence)",
  "official land title (indicative)",
  "official right of use (indicative)",
  "local government documentation (indicative)",
  // d5 (a, c, e)
  "official land title or right-of-use",
  "official right of use in restricted use area",
  "indicative land tenure with illegality screening completed",
]);

export const getEudrValueColor = (data?: string | null): string | undefined => {
  if (!data) return undefined;
  const value = data.trim().toLowerCase();
  if (EUDR_RED_VALUES.has(value)) return "red";
  if (EUDR_BLUE_VALUES.has(value)) return "blue";
  return undefined;
};
